import { useId } from 'react';

import { cn } from '../../lib/utils';
import { Checkbox } from '../shadcn/checkbox';
import { RadioGroup, RadioGroupItem } from '../shadcn/radio-group';
import type { SelectOption } from '../select';

/**
 * A short, fixed list of options, every one of them on the page — one pick
 * with radios, or several with `multiple` and checkboxes.
 *
 * The choice to reach for when the options fit on screen: nothing has to be
 * opened before it can be read. For a long list in a tight spot, `MultiSelect`
 * keeps them in a dropdown instead.
 *
 * Takes what `Field` hands a control, so it binds the same way:
 * `<Field name="source" label="…"><ChoiceList options={…} /></Field>`. The
 * value is a string with one pick and a `string[]` with `multiple`; anything
 * else (Field's `''` for an unset value) reads as nothing picked.
 *
 * Each option is a full-width row and the whole row is the target, so on a
 * phone the aim is the row's 44px, not the 16px box inside it.
 */
export function ChoiceList({
  id,
  value,
  options,
  multiple = false,
  plain = false,
  onChange,
  onBlur,
  disabled,
  className,
  ref,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  id?: string;
  value?: unknown;
  options: readonly SelectOption[];
  /** Several picks, drawn as checkboxes. Without it, one pick, drawn as radios. */
  multiple?: boolean;
  /**
   * Just the control and its words: no box, no fill, no border around each
   * row. For two or three short options that read better as a line of radios
   * than as a stack of cards.
   */
  plain?: boolean;
  /** A string with one pick, a `string[]` with `multiple`. */
  onChange?: (next: string | string[]) => void;
  /** Fires when focus leaves the whole list, not when it moves between rows. */
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  /** Lands on the first option, so a form can focus the list when it is wrong on submit. */
  ref?: React.Ref<HTMLButtonElement>;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  const baseId = useId();
  const optionId = (i: number) => `${baseId}-${i}`;

  const picked = multiple
    ? Array.isArray(value)
      ? (value as string[])
      : []
    : typeof value === 'string'
      ? value
      : '';

  // Blur from one row to the next is still inside the list; only leaving it
  // counts as the field being touched.
  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlur?.();
  };

  const rowCls = plain
    ? cn(
        // A line of text, no taller: the radio and its word are both the
        // target, so a phone still has the word to aim at.
        // `text-sm` (13px) at every size: plain radios sit inline beside a
        // label and should read as quieter than the boxes above them.
        'flex cursor-pointer items-center gap-2 text-sm',
        'has-[:disabled]:cursor-not-allowed has-[:disabled]:text-ink-disabled',
      )
    : cn(
        'flex min-h-(--target-min) cursor-pointer items-center gap-3 rounded-input border border-hairline bg-surface px-3.5 py-2 text-sm md:text-base',
        'transition-colors duration-(--duration-fast) ease-standard',
        'hover:border-brand-edge',
        'has-[[data-state=checked]]:border-brand-edge has-[[data-state=checked]]:bg-brand-wash',
        'has-[:disabled]:cursor-not-allowed has-[:disabled]:border-hairline has-[:disabled]:bg-raised has-[:disabled]:text-ink-disabled',
        ariaInvalid && 'border-destructive',
      );

  const groupProps = {
    id,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    onBlur: handleBlur,
    className: cn('flex flex-col gap-2', className),
  };

  if (multiple) {
    const list = picked as string[];
    const toggle = (optionValue: string, on: boolean) =>
      onChange?.(
        // Rebuilt from `options` so the stored order matches the list, not the order of clicks.
        options
          .map((o) => o.value)
          .filter((v) => (v === optionValue ? on : list.includes(v))),
      );

    return (
      <div role="group" {...groupProps}>
        {options.map((option, i) => (
          <label key={option.value} htmlFor={optionId(i)} className={rowCls}>
            <Checkbox
              id={optionId(i)}
              ref={i === 0 ? ref : undefined}
              checked={list.includes(option.value)}
              onCheckedChange={(state) => toggle(option.value, state === true)}
              disabled={disabled}
              aria-invalid={ariaInvalid}
            />
            {option.label}
          </label>
        ))}
      </div>
    );
  }

  return (
    <RadioGroup
      {...groupProps}
      value={picked as string}
      onValueChange={(next) => onChange?.(next)}
      disabled={disabled}
    >
      {options.map((option, i) => (
        <label key={option.value} htmlFor={optionId(i)} className={rowCls}>
          <RadioGroupItem
            id={optionId(i)}
            ref={i === 0 ? ref : undefined}
            value={option.value}
            aria-invalid={ariaInvalid}
          />
          {option.label}
        </label>
      ))}
    </RadioGroup>
  );
}
